/**
 * Geocodificador especializado para infraestructuras hidráulicas de Andalucía
 * 
 * Conecta con servicios WFS de REDIAM para acceder a 9 capas de infraestructuras:
 * - ms:edar - Estaciones Depuradoras de Aguas Residuales (~450)
 * - ms:etap - Estaciones de Tratamiento de Aguas Potables
 * - ms:embalses - Embalses y presas (~80)
 * - ms:depositos - Depósitos de agua
 * - ms:potabilizadoras - Plantas potabilizadoras
 * - ms:conducciones - Conducciones de agua
 * - ms:colectores_saneamiento - Colectores de saneamiento
 * - ms:estaciones_elevadoras_ar - Estaciones Elevadoras Agua Residual
 * - ms:estaciones_elevadoras_ap - Estaciones Elevadoras Agua Potable
 * 
 * Endpoint WFS verificado 2026-01-22:
 * https://www.juntadeandalucia.es/medioambiente/mapwms/REDIAM_WFS_SP_Infraestructuras_Hidraulicas
 * 
 * @module services/geocoding/specialized
 */

import type { 
  WFSFeature, 
  WFSSearchOptions 
} from './WFSBaseGeocoder';
import { 
  WFSBaseGeocoder 
} from './WFSBaseGeocoder';
import type { GeoJSONFeature } from '../../../types/geojson';
import { getStringProp as _getStringProp } from '../../../types/geojson';
import type { SpecializedGeocoderConfig, GeocodingResult } from '../../../types/infrastructure';
import { getBBoxByINEinUTM30, type BBox as _BBox } from '../../boundaries';

/**
 * Tipos de infraestructuras hidráulicas disponibles en WFS REDIAM
 * 
 * Verificado 2026-01-22 via GetCapabilities
 */
export enum HydraulicFacilityType {
  /** Estaciones Depuradoras de Aguas Residuales */
  EDAR = 'EDAR',
  /** Estaciones de Tratamiento de Aguas Potables */
  ETAP = 'ETAP',
  /** Embalses y presas */
  EMBALSE = 'EMBALSE',
  /** Depósitos de agua */
  DEPOSITO = 'DEPOSITO',
  /** Plantas potabilizadoras */
  POTABILIZADORA = 'POTABILIZADORA',
  /** Conducciones de agua */
  CONDUCCION = 'CONDUCCION',
  /** Colectores de saneamiento */
  COLECTOR = 'COLECTOR',
  /** Estaciones Elevadoras de Agua Residual */
  ELEVADORA_AR = 'ELEVADORA_AR',
  /** Estaciones Elevadoras de Agua Potable */
  ELEVADORA_AP = 'ELEVADORA_AP'
}

/**
 * Mapeo de tipos de infraestructura a nombres de capas WFS reales
 * 
 * IMPORTANTE: Los nombres de capas llevan prefijo "ms:" obligatorio
 * Verificado via GetCapabilities 2026-01-22
 */
export const HYDRAULIC_LAYER_MAP: Record<HydraulicFacilityType, string> = {
  [HydraulicFacilityType.EDAR]: 'ms:edar',
  [HydraulicFacilityType.ETAP]: 'ms:etap',
  [HydraulicFacilityType.EMBALSE]: 'ms:embalses',
  [HydraulicFacilityType.DEPOSITO]: 'ms:depositos',
  [HydraulicFacilityType.POTABILIZADORA]: 'ms:potabilizadoras',
  [HydraulicFacilityType.CONDUCCION]: 'ms:conducciones',
  [HydraulicFacilityType.COLECTOR]: 'ms:colectores_saneamiento',
  [HydraulicFacilityType.ELEVADORA_AR]: 'ms:estaciones_elevadoras_ar',
  [HydraulicFacilityType.ELEVADORA_AP]: 'ms:estaciones_elevadoras_ap'
};

/**
 * Esquema de campos por tipo de capa WFS
 * 
 * Cada capa tiene campos diferentes - verificados via DescribeFeatureType
 */
export interface HydraulicLayerSchema {
  /** Campo para el nombre de la infraestructura */
  nameField: string;
  /** Campo para código identificador (opcional) */
  codeField?: string;
  /** Campo para estado operativo (opcional) */
  statusField?: string;
  /** Campo para caudal máximo (opcional) */
  flowMaxField?: string;
  /** Campo para caudal medio (opcional) */
  flowMedField?: string;
  /** Campos adicionales específicos de la capa */
  additionalFields?: string[];
}

/**
 * Esquemas de campos verificados por capa WFS
 * 
 * Verificado via DescribeFeatureType 2026-01-22
 */
export const HYDRAULIC_LAYER_SCHEMAS: Record<string, HydraulicLayerSchema> = {
  'ms:edar': {
    nameField: 'NOMBRE',
    codeField: 'CODIGO',
    statusField: 'ESTADO',
    flowMaxField: 'QMAX',
    flowMedField: 'QMED',
    additionalFields: ['RED', 'SITUACION', 'DESTINO', 'COTA', 'TIPO_ROTU']
  },
  'ms:etap': {
    nameField: 'NOMBRE',
    codeField: 'CODIGO',
    statusField: 'ESTADO',
    flowMaxField: 'QMAX',
    flowMedField: 'QMED',
    additionalFields: ['RED', 'SITUACION', 'PROCEDENCI', 'DESTINO', 'COTA', 
                       'QUNI', 'TIPO_DEC', 'VOL_AREA', 'N__FILTROS', 
                       'TIPO_FIL', 'TIPO_CLO', 'TIPO_ROTU', 'QM3_H']
  },
  'ms:embalses': {
    nameField: 'Nombre_del_embalse',
    codeField: 'Codigo_de_embalse',
    additionalFields: ['Codigo_de_estacion_SAIH', 'Codigo_ROEA_del_embalse',
                       'Area_del_contorno_del_embalse', 'Perimetro_del_contorno_del_embalse',
                       'Fecha_de_inclusion_en_el_inventario']
  },
  'ms:depositos': {
    nameField: 'NOMBRE',
    codeField: 'CODIGO',
    statusField: 'ESTADO'
  },
  'ms:potabilizadoras': {
    nameField: 'NOMBRE',
    codeField: 'CODIGO',
    statusField: 'ESTADO'
  },
  'ms:conducciones': {
    nameField: 'NOMBRE',
    codeField: 'CODIGO'
  },
  'ms:colectores_saneamiento': {
    nameField: 'NOMBRE',
    codeField: 'CODIGO'
  },
  'ms:estaciones_elevadoras_ar': {
    nameField: 'NOMBRE',
    codeField: 'CODIGO',
    statusField: 'ESTADO'
  },
  'ms:estaciones_elevadoras_ap': {
    nameField: 'NOMBRE',
    codeField: 'CODIGO',
    statusField: 'ESTADO'
  }
};

/**
 * Opciones de búsqueda específicas para infraestructuras hidráulicas
 */
export interface HydraulicSearchOptions extends WFSSearchOptions {
  /** Tipo de infraestructura hidráulica */
  facilityType?: HydraulicFacilityType;
  /** Código INE del municipio (5 dígitos) para filtrado por BBOX */
  municipalityCode?: string;
  /** Cuenca hidrográfica (no disponible en todas las capas) */
  basin?: string;
  /** Caudal mínimo m³/día */
  minFlow?: number;
  /** Estado operativo */
  status?: string;
}

/**
 * Geocodificador especializado para infraestructuras hidráulicas andaluzas
 * 
 * Precisión esperada: ±5-15m (coordenadas oficiales REDIAM)
 * Cobertura: ~850+ infraestructuras hidráulicas en 9 capas
 * 
 * NOTA: Las capas WFS de REDIAM NO incluyen campos MUNICIPIO ni PROVINCIA.
 * La geocodificación se basa en coincidencia por nombre y proximidad espacial.
 * 
 * @example
 * ```typescript
 * const geocoder = new WFSHydraulicGeocoder();
 * const result = await geocoder.geocode({
 *   name: 'EDAR Granada Sur',
 *   facilityType: HydraulicFacilityType.EDAR
 * });
 * ```
 */
export class WFSHydraulicGeocoder extends WFSBaseGeocoder {
  
  /** Capa actualmente seleccionada */
  private currentLayer: string = 'ms:edar';

  /**
   * Configuración específica para WFS REDIAM Infraestructuras Hidráulicas
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      wfsEndpoint: 'https://www.juntadeandalucia.es/medioambiente/mapwms/REDIAM_WFS_SP_Infraestructuras_Hidraulicas',
      layerName: 'ms:edar', // Capa por defecto - NO usar this.currentLayer (no inicializado aún)
      fuzzyThreshold: 0.35,
      timeout: 20000, // 20s - REDIAM puede ser lento
      outputSRS: 'EPSG:25830'
    };
  }

  /**
   * Obtiene el esquema de campos para la capa actual
   */
  private getLayerSchema(): HydraulicLayerSchema {
    return HYDRAULIC_LAYER_SCHEMAS[this.currentLayer] || {
      nameField: 'NOMBRE',
      codeField: 'CODIGO'
    };
  }

  /**
   * Parsea feature GeoJSON de REDIAM Hidráulicas
   *
   * Geometrías aceptadas (post-sub-WHLG, vía WFSBaseGeocoder.parseGeometry):
   * - Point (capas puntuales: edar, etap, depositos, potabilizadoras, elevadoras)
   * - MultiPoint(N=1) cross-tipología (defensivo, REDIAM productivo no devuelve)
   * - Polygon (embalses, smoke 12-may 100 % Polygon puro)
   * - MultiPolygon(N=1) (defensivo, REDIAM productivo no devuelve)
   * - LineString (conducciones smoke 13-may 100 % LineString puro 2-77 verts,
   *   colectores_saneamiento 100 % LineString puro 2-48 verts)
   * - MultiLineString(N=1) cross-tipología (defensivo)
   *
   * Geometrías RECHAZADAS con warn (multipieza ambiguas):
   * - MultiPoint(N>1) / MultiPolygon(N>1) / MultiLineString(N>1)
   *
   * Cierre completo del cluster sub-0a al 100 % cross-tipología cross-endpoint
   * cross-geometría: 8/8 geocoders especializados consumen parseGeometry()
   * uniformemente sobre Point + MultiPoint + Polygon + MultiPolygon +
   * LineString + MultiLineString (CDAV + REDIAM hidráulicas + AAE Energy +
   * DERA varios unificados).
   *
   * IMPORTANTE: No hay campos MUNICIPIO/PROVINCIA en estas capas REDIAM.
   */
  protected parseFeature(feature: GeoJSONFeature): WFSFeature | null {
    try {
      const props = feature.properties || {};
      const geom = feature.geometry;

      // Punto / MultiPoint(N=1) / Polygon / MultiPolygon(N=1) vía primitiva
      // centralizada de WFSBaseGeocoder (sub-0a WMPF + sub-WEMB + sub-WHUP).
      const pt = this.parseGeometry(geom, `Hydraulic feature ${String(feature.id ?? 'sin-id')}`);
      if (!pt) return null;
      const [x, y] = pt;

      // Validar coordenadas UTM30 Andalucía (específico PTEL, fuera del helper)
      if (x < 100000 || x > 800000 || y < 4000000 || y > 4300000) {
        console.warn('Coordenadas fuera de rango UTM30:', x, y);
        return null;
      }

      // Obtener esquema de campos para esta capa
      const schema = this.getLayerSchema();

      // Extraer nombre según el campo correcto de la capa
      const name = String(props[schema.nameField] || '');
      
      return {
        name,
        x,
        y,
        municipality: '', // NO disponible en WFS REDIAM hidráulicas
        province: '', // NO disponible en WFS REDIAM hidráulicas
        address: '', // Infraestructuras hidráulicas no tienen dirección postal
        properties: {
          code: schema.codeField ? props[schema.codeField] || '' : '',
          status: schema.statusField ? props[schema.statusField] : '',
          flowMax: schema.flowMaxField ? props[schema.flowMaxField] : '',
          flowMed: schema.flowMedField ? props[schema.flowMedField] : '',
          layerType: this.currentLayer,
          ...props
        }
      };
    } catch (error) {
      console.error('Error parseando feature hidráulica:', error);
      return null;
    }
  }

  /**
   * Construye parámetros WFS con soporte para municipalityCode
   * 
   * Sobreescribe el método padre para usar HydraulicSearchOptions
   */
  protected buildWFSParams(options: HydraulicSearchOptions): Record<string, string> {
    const params: Record<string, string> = {
      service: 'WFS',
      version: '2.0.0',
      request: 'GetFeature',
      typeName: this.config.layerName,
      // CRÍTICO: REDIAM `/medioambiente/mapwms/REDIAM_WFS_*` es MapServer (no
      // GeoServer pese al path /mapwms/) y rechaza el alias corto con HTTP 400
      // `msWFSGetFeature(): WFS server error. 'application/json' is not a
      // permitted output format for layer X`. Override del default del Base
      // (`application/json` para GeoServer estándar) confirmado empiricamente
      // en smoke sub-WUF 14-may sobre ms:edar. Dicotomía estructural por
      // implementación del servidor, no drift. Ref: JSDoc del Base.buildWFSParams.
      outputFormat: 'application/json; subtype=geojson',
      srsName: this.config.outputSRS,
      count: String(options.maxResults || 100)  // WFS 2.0 canónico, migrado en sub-WMVC 14-may
    };

    // Agregar filtro CQL si existe
    const cqlFilter = this.buildCQLFilter(options);
    if (cqlFilter) {
      params['CQL_FILTER'] = cqlFilter;
    }

    return params;
  }

  /**
   * Construye filtro CQL para búsqueda en infraestructuras hidráulicas
   * 
   * Estrategia BBOX + Nombre:
   * - Si hay municipalityCode: añadir BBOX del municipio para filtrado espacial
   * - Siempre filtrar por NOMBRE con ILIKE
   * 
   * Esto reduce datos transferidos 10-20x y elimina falsos positivos geográficos
   */
  protected buildCQLFilter(options: HydraulicSearchOptions): string {
    const filters: string[] = [];
    const schema = this.getLayerSchema();

    // BBOX del municipio si hay código INE (F2.T2.2)
    if (options.municipalityCode) {
      const bboxUTM = getBBoxByINEinUTM30(options.municipalityCode);
      if (bboxUTM) {
        const [minX, minY, maxX, maxY] = bboxUTM;
        // Campo geométrico puede variar por capa, 'the_geom' es común en GeoServer
        filters.push(`BBOX(the_geom,${minX},${minY},${maxX},${maxY},'EPSG:25830')`);
      }
    } else if (options.bbox) {
      // BBOX explícito (ya en UTM30)
      const [minX, minY, maxX, maxY] = options.bbox;
      filters.push(`BBOX(the_geom,${minX},${minY},${maxX},${maxY},'EPSG:25830')`);
    }

    // Filtro por nombre usando el campo correcto de la capa
    if (options.name) {
      filters.push(`${schema.nameField} ILIKE '%${this.escapeCQL(options.name)}%'`);
    }

    // Filtro por estado si la capa lo soporta
    if (options.status && schema.statusField) {
      filters.push(`${schema.statusField} ILIKE '%${this.escapeCQL(options.status)}%'`);
    }

    // Filtro por caudal mínimo si la capa lo soporta
    if (options.minFlow && schema.flowMaxField) {
      filters.push(`${schema.flowMaxField} >= '${options.minFlow}'`);
    }

    return filters.length > 0 ? filters.join(' AND ') : '';
  }

  /**
   * Detecta tipo de infraestructura hidráulica por nombre
   * 
   * @param name Nombre de la infraestructura a clasificar
   * @returns Tipo detectado o EDAR como fallback
   */
  private detectFacilityType(name: string): HydraulicFacilityType {
    const nameLower = name.toLowerCase();
    
    if (nameLower.includes('edar') || nameLower.includes('depuradora')) {
      return HydraulicFacilityType.EDAR;
    }
    if (nameLower.includes('etap') || nameLower.includes('tratamiento agua potable')) {
      return HydraulicFacilityType.ETAP;
    }
    if (nameLower.includes('embalse') || nameLower.includes('presa') || nameLower.includes('pantano')) {
      return HydraulicFacilityType.EMBALSE;
    }
    if (nameLower.includes('depósito') || nameLower.includes('deposito')) {
      return HydraulicFacilityType.DEPOSITO;
    }
    if (nameLower.includes('potabilizadora')) {
      return HydraulicFacilityType.POTABILIZADORA;
    }
    if (nameLower.includes('elevadora')) {
      // Distinguir entre AR y AP
      if (nameLower.includes('residual') || nameLower.includes('ar')) {
        return HydraulicFacilityType.ELEVADORA_AR;
      }
      return HydraulicFacilityType.ELEVADORA_AP;
    }
    if (nameLower.includes('colector') || nameLower.includes('saneamiento')) {
      return HydraulicFacilityType.COLECTOR;
    }
    if (nameLower.includes('conducción') || nameLower.includes('conduccion')) {
      return HydraulicFacilityType.CONDUCCION;
    }
    
    return HydraulicFacilityType.EDAR; // Default
  }

  /**
   * Selecciona la capa WFS según el tipo de infraestructura
   * 
   * @param facilityType Tipo de infraestructura o auto-detectar
   * @param name Nombre para auto-detección si no se especifica tipo
   */
  private selectLayer(facilityType?: HydraulicFacilityType, name?: string): void {
    let type: HydraulicFacilityType;
    
    if (facilityType) {
      type = facilityType;
    } else if (name) {
      type = this.detectFacilityType(name);
    } else {
      type = HydraulicFacilityType.EDAR;
    }
    
    this.currentLayer = HYDRAULIC_LAYER_MAP[type];
    this.config.layerName = this.currentLayer;
  }

  /**
   * Geocodifica con selección automática de capa según nombre/tipo
   * 
   * Intenta primero con la capa detectada, luego prueba otras si no encuentra
   * 
   * @param options Opciones de búsqueda
   * @returns Resultado de geocodificación o null
   */
  public async geocodeWithAutoLayer(options: HydraulicSearchOptions): Promise<GeocodingResult | null> {
    // Detectar tipo y seleccionar capa inicial
    this.selectLayer(options.facilityType, options.name);

    // Intentar con la capa detectada
    let result = await this.geocodeLegacy(options);
    
    if (result && result.confidence >= 70) {
      return result;
    }

    // Si no encuentra, probar capas principales
    const layersToTry: HydraulicFacilityType[] = [
      HydraulicFacilityType.EDAR,
      HydraulicFacilityType.ETAP,
      HydraulicFacilityType.EMBALSE,
      HydraulicFacilityType.DEPOSITO,
      HydraulicFacilityType.POTABILIZADORA
    ];
    
    for (const type of layersToTry) {
      const layer = HYDRAULIC_LAYER_MAP[type];
      if (layer === this.currentLayer) continue;
      
      this.currentLayer = layer;
      this.config.layerName = layer;
      
      result = await this.geocodeLegacy(options);
      
      if (result && result.confidence >= 70) {
        return result;
      }
    }

    // Restaurar capa por defecto
    this.currentLayer = 'ms:edar';
    this.config.layerName = 'ms:edar';
    return result;
  }

  /**
   * Obtiene todas las infraestructuras hidráulicas de un municipio usando BBOX
   * 
   * Usa filtrado espacial por BBOX del municipio para obtener solo infraestructuras
   * dentro de sus límites administrativos. Mucho más eficiente que filtrar por nombre.
   * 
   * @param municipalityCode Código INE del municipio (5 dígitos)
   * @returns Array de features encontradas en todas las capas
   * 
   * @example
   * const features = await geocoder.getAllFacilitiesInMunicipalityByCode('18054');
   * // Retorna todas las EDAR, ETAP, embalses, etc. de Colomera
   */
  public async getAllFacilitiesInMunicipalityByCode(
    municipalityCode: string
  ): Promise<WFSFeature[]> {
    const allFeatures: WFSFeature[] = [];
    
    // Capas principales a consultar
    const layersToQuery = [
      'ms:edar',
      'ms:etap',
      'ms:embalses',
      'ms:depositos',
      'ms:potabilizadoras'
    ];

    for (const layer of layersToQuery) {
      try {
        this.currentLayer = layer;
        this.config.layerName = layer;
        
        const params = this.buildWFSParams({
          name: '', // Sin filtro por nombre, solo BBOX
          municipalityCode, // Usar BBOX del municipio
          maxResults: 100
        });

        const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
        const features = this.parseResponse(response.data);
        
        // Añadir info de capa a cada feature
        features.forEach(f => {
          f.properties.sourceLayer = layer;
        });
        
        allFeatures.push(...features);

      } catch (error) {
        console.warn(`Error obteniendo ${layer} en municipio ${municipalityCode}:`, error);
      }
    }

    // Restaurar capa por defecto
    this.currentLayer = 'ms:edar';
    this.config.layerName = 'ms:edar';
    
    return allFeatures;
  }

  /**
   * Geocodifica una infraestructura dentro de un municipio específico
   * 
   * Usa estrategia BBOX + Nombre para:
   * - Reducir datos transferidos 10-20x
   * - Eliminar falsos positivos de otros municipios
   * - Mayor precisión en el matching fuzzy
   * 
   * @param name Nombre de la infraestructura a buscar
   * @param municipalityCode Código INE del municipio (5 dígitos)
   * @param facilityType Tipo de infraestructura (opcional, auto-detecta si no se especifica)
   * @returns Resultado de geocodificación o null
   * 
   * @example
   * const result = await geocoder.geocodeInMunicipality(
   *   'EDAR',
   *   '18054', // Colomera
   *   HydraulicFacilityType.EDAR
   * );
   */
  public async geocodeInMunicipality(
    name: string,
    municipalityCode: string,
    facilityType?: HydraulicFacilityType
  ): Promise<GeocodingResult | null> {
    // Seleccionar capa según tipo
    this.selectLayer(facilityType, name);
    
    // Geocodificar con BBOX del municipio
    // Nota: Usamos geocode() que acepta WFSSearchOptions,
    // pero buildWFSParams/buildCQLFilter están sobreescritos para HydraulicSearchOptions
    const result = await this.geocodeLegacy({
      name,
      bbox: getBBoxByINEinUTM30(municipalityCode) || undefined
    } as HydraulicSearchOptions);
    
    return result;
  }

  /**
   * Obtiene todas las infraestructuras hidráulicas de un municipio
   * 
   * @deprecated Usar getAllFacilitiesInMunicipalityByCode() con código INE
   * 
   * @param municipality Nombre del municipio
   * @param province Nombre de la provincia (opcional)
   * @returns Array de features encontradas
   */
  public async getAllFacilitiesInMunicipality(
    municipality: string,
    _province?: string
  ): Promise<WFSFeature[]> {
    const allFeatures: WFSFeature[] = [];
    
    // Capas principales a consultar
    const layersToQuery = [
      'ms:edar',
      'ms:etap',
      'ms:embalses',
      'ms:depositos',
      'ms:potabilizadoras'
    ];

    for (const layer of layersToQuery) {
      try {
        this.currentLayer = layer;
        this.config.layerName = layer;
        
        const params = this.buildWFSParams({
          name: municipality, // Buscar por nombre que contenga el municipio
          maxResults: 100
        });

        const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
        const features = this.parseResponse(response.data);
        
        // Añadir info de capa a cada feature
        features.forEach(f => {
          f.properties.sourceLayer = layer;
        });
        
        allFeatures.push(...features);

      } catch (error) {
        console.warn(`Error obteniendo ${layer} de ${municipality}:`, error);
      }
    }

    // Restaurar capa por defecto
    this.currentLayer = 'ms:edar';
    this.config.layerName = 'ms:edar';
    
    return allFeatures;
  }

  /**
   * Lista todas las capas disponibles
   */
  public getAvailableLayers(): string[] {
    return Object.values(HYDRAULIC_LAYER_MAP);
  }

  /**
   * Obtiene información del esquema de una capa
   */
  public getLayerInfo(layerName: string): HydraulicLayerSchema | undefined {
    return HYDRAULIC_LAYER_SCHEMAS[layerName];
  }
}

export default WFSHydraulicGeocoder;
